import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Microphone, Stop, UploadSimple, Waveform,
  PlayCircle, Trash, PencilSimple, GlobeHemisphereWest, Lock,
  TextT, Star, MagicWand,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import VoiceEffectsPanel from "../components/VoiceEffectsPanel";
import {
  installVoiceEnhancer,
  setVoiceEnhancement,
  isVoiceEnhancementEnabled,
} from "../lib/voiceEnhancer";

const KIND_OPTIONS = [
  { value: "recording",    label: "Recording" },
  { value: "song",         label: "Song" },
  { value: "podcast",      label: "Podcast" },
  { value: "spoken_word",  label: "Spoken Word" },
  { value: "narration",    label: "Narration" },
  { value: "education",    label: "Education" },
  { value: "business",     label: "Business" },
  { value: "conference",   label: "Conference" },
  { value: "meeting",      label: "Meeting" },
];

function fmtDuration(seconds) {
  if (!seconds || Number.isNaN(seconds)) return "—";
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

export default function VoiceStudio() {
  const nav = useNavigate();
  const [proStatus, setProStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // recorder state
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [preview, setPreview] = useState(null); // { url, blob, mime, ext }

  // upload form state
  const [kind, setKind] = useState("recording");
  const [title, setTitle] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [enhanceLive, setEnhanceLive] = useState(true);
  const [showFx, setShowFx] = useState(false);

  // Install the WebAudio enhancer once — same singleton that GoLive uses. On this
  // page we default it ON so recordings sound cleaner; the user can flip it off
  // to capture their raw voice for post-processing.
  useEffect(() => {
    installVoiceEnhancer();
    setVoiceEnhancement(enhanceLive);
  }, [enhanceLive]);

  const load = async () => {
    try {
      const [{ data: mine }, { data: pro }] = await Promise.all([
        api.get("/voice/me"),
        api.get("/voice-pro/status"),
      ]);
      setItems(mine || []);
      setProStatus(pro);
    } finally { setLoading(false); }
  };

  // Auto-capture PayPal on return: PayPal redirects back with ?token=<order_id>
  // when the user finishes checkout. We also stash the order_id in localStorage
  // as a belt-and-suspenders in case the query string is missing.
  useEffect(() => {
    const url = new URL(window.location.href);
    const paypalToken = url.searchParams.get("token") || url.searchParams.get("paypal_order_id");
    const stashed = localStorage.getItem("voice_pro_paypal_order_id");
    const orderId = paypalToken || stashed;
    if (!orderId) return;
    (async () => {
      try {
        const { data } = await api.post("/voice-pro/capture", { order_id: orderId });
        if (data.status === "completed" || data.is_voice_pro) {
          toast.success("Voice Pro activated — enjoy!");
        }
      } catch (e) {
        // Silent — capture is idempotent; a stale token in the URL isn't fatal.
      } finally {
        localStorage.removeItem("voice_pro_paypal_order_id");
        url.searchParams.delete("token");
        url.searchParams.delete("paypal_order_id");
        url.searchParams.delete("PayerID");
        window.history.replaceState({}, "", url.pathname + (url.search || ""));
        load();
      }
    })();
  }, []); // eslint-disable-line

  useEffect(() => { load(); return () => stopStream(); }, []); // eslint-disable-line

  // -------- recording --------
  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        const url = URL.createObjectURL(blob);
        const ext = mime.startsWith("audio/mp4") ? "m4a" : "webm";
        setPreview({ url, blob, mime, ext });
        stopStream();
        setRecording(false);
      };
      rec.start();
      mediaRecorderRef.current = rec;
      setRecSeconds(0);
      timerRef.current = setInterval(() => setRecSeconds((n) => n + 1), 1000);
      setRecording(true);
    } catch (e) {
      toast.error(e.message?.includes("Permission") ? "Microphone permission denied." : "Could not start recording.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  const discardPreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null); setRecSeconds(0);
  };

  const saveRecording = async () => {
    if (!preview) return;
    setUploading(true);
    try {
      const fd = new FormData();
      const filename = `voice.${preview.ext}`;
      fd.append("file", preview.blob, filename);
      fd.append("title", title || `Voice ${new Date().toLocaleString()}`);
      fd.append("kind", kind);
      fd.append("is_public", isPublic);
      fd.append("duration_sec", String(recSeconds));
      await api.post("/voice/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Recording saved to your library.");
      discardPreview();
      setTitle("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  // -------- file upload --------
  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      fd.append("kind", kind);
      fd.append("is_public", isPublic);
      await api.post("/voice/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded.`);
      setTitle("");
      e.target.value = "";
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  // -------- library actions --------
  const [renamingId, setRenamingId] = useState(null);
  const [renameText, setRenameText] = useState("");

  const commitRename = async (id) => {
    try {
      await api.put(`/voice/${id}`, { title: renameText.trim() || null });
      setRenamingId(null); setRenameText(""); await load();
    } catch (e) { toast.error("Rename failed"); }
  };

  const togglePublic = async (rec) => {
    try {
      await api.put(`/voice/${rec.id}`, { is_public: !rec.is_public });
      await load();
    } catch (e) { toast.error("Update failed"); }
  };

  const deleteRec = async (id) => {
    if (!window.confirm("Delete this recording? This cannot be undone.")) return;
    try { await api.delete(`/voice/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const transcribe = async (rec) => {
    try {
      toast.info("Transcribing… this may take a moment.");
      const { data } = await api.post(`/voice/${rec.id}/transcribe`);
      toast.success("Transcript ready.");
      await load();
      if (data.transcript) window.alert(data.transcript.slice(0, 800));
    } catch (e) { toast.error(e?.response?.data?.detail || "Transcription unavailable (need OpenAI billing)."); }
  };

  const subscribePro = async (mode) => {
    if (mode === "waves") {
      if (!window.confirm(`Spend ${proStatus?.price_waves || 999} waves for 30 days of Voice Pro?`)) return;
      try {
        await api.post("/voice-pro/subscribe-waves");
        toast.success("You are now Voice Pro for 30 days.");
        await load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Subscribe failed"); }
    } else {
      // PayPal: create order, stash id, redirect to PayPal approval URL.
      try {
        const { data } = await api.post("/voice-pro/order");
        if (!data?.order_id) throw new Error("No order id returned");
        localStorage.setItem("voice_pro_paypal_order_id", data.order_id);
        toast.info("Redirecting to PayPal…");
        // Sandbox/prod approve URL — the /voice page auto-captures on return
        // by reading ?token=<order_id> and calling /voice-pro/capture.
        const approveBase = process.env.REACT_APP_PAYPAL_MODE === "live"
          ? "https://www.paypal.com/checkoutnow"
          : "https://www.sandbox.paypal.com/checkoutnow";
        window.location.href = `${approveBase}?token=${data.order_id}`;
      } catch (e) {
        toast.error(e?.response?.data?.detail || "PayPal order failed");
      }
    }
  };

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="voice-studio-page">
      {/* Header */}
      <div className="px-5 pt-6 flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream"
          data-testid="voice-back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Voice Studio</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">Record. Upload. Enhance.</h1>
        </div>
      </div>

      {/* Pro banner */}
      <div className="mx-5 mt-5 rounded-3xl bg-gradient-to-r from-papaya/20 to-teal-500/20 border border-papaya/30 p-4" data-testid="voice-pro-banner">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Star weight="fill" size={16} className="text-papaya" />
              <p className="text-cream font-black uppercase tracking-[0.2em] text-xs">
                Voice Enhancement Pro
              </p>
            </div>
            <p className="text-cream/70 text-sm mt-1">
              Real-time voice changer + reverb (Voice Effects Library), AI vocal repair, meeting enhancement, real-time captions.
            </p>
            {proStatus?.is_voice_pro ? (
              <p className="text-teal-300 text-xs mt-2 font-bold" data-testid="voice-pro-active">
                Active — renews {new Date(proStatus.voice_pro_until).toLocaleDateString()}
              </p>
            ) : (
              <p className="text-cream/40 text-xs mt-2">
                ${proStatus?.price_usd || 9.99}/month · or {proStatus?.price_waves || 999} waves
              </p>
            )}
          </div>
          {!proStatus?.is_voice_pro && (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => subscribePro("waves")}
                className="rounded-full bg-papaya text-ink text-[11px] px-4 py-2 font-black tracking-[0.18em] uppercase hover:brightness-110"
                data-testid="voice-pro-subscribe-waves"
              >Pay Waves</button>
              <button
                onClick={() => subscribePro("paypal")}
                className="rounded-full bg-white/10 text-cream text-[11px] px-4 py-2 font-black tracking-[0.18em] uppercase hover:bg-white/20"
                data-testid="voice-pro-subscribe-paypal"
              >PayPal</button>
            </div>
          )}
        </div>
      </div>

      {/* Recorder + Upload */}
      <div className="mx-5 mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Recorder */}
        <div className="rounded-3xl crystal-glass p-5" data-testid="voice-recorder">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Waveform size={18} className="text-papaya" />
              <p className="text-cream font-black uppercase tracking-[0.2em] text-xs">Record now</p>
            </div>
            <button
              onClick={() => setShowFx(true)}
              className="rounded-full bg-papaya/20 text-papaya px-3 py-1.5 text-[10px] font-black tracking-[0.15em] uppercase flex items-center gap-1 hover:bg-papaya/30"
              data-testid="voice-studio-effects-btn"
            >
              <MagicWand size={12} weight="fill" /> Voice FX
            </button>
          </div>
          {!preview ? (
            <>
              <div className={`flex items-center justify-center mt-2 transition-all ${recording ? "text-red-500" : "text-cream"}`}>
                <span className={`text-4xl font-black tracking-tighter tabular-nums ${recording ? "animate-pulse" : ""}`}>
                  {String(Math.floor(recSeconds / 60)).padStart(2, "0")}:{String(recSeconds % 60).padStart(2, "0")}
                </span>
              </div>
              <div className="flex justify-center mt-3">
                {!recording ? (
                  <button
                    onClick={startRecording}
                    className="rounded-full bg-papaya text-ink w-16 h-16 flex items-center justify-center hover:brightness-110 papaya-glow"
                    data-testid="voice-record-start"
                  >
                    <Microphone size={26} weight="fill" />
                  </button>
                ) : (
                  <button
                    onClick={stopRecording}
                    className="rounded-full bg-red-500 text-white w-16 h-16 flex items-center justify-center hover:brightness-110"
                    data-testid="voice-record-stop"
                  >
                    <Stop size={26} weight="fill" />
                  </button>
                )}
              </div>
              <p className="text-cream/40 text-[10px] tracking-[0.3em] uppercase text-center mt-4">
                Tap to {recording ? "stop" : "start"} — mic required
              </p>
            </>
          ) : (
            <div className="mt-2" data-testid="voice-preview">
              <audio src={preview.url} controls className="w-full mt-1" />
              <div className="flex gap-2 mt-3">
                <button
                  onClick={saveRecording}
                  disabled={uploading}
                  className="flex-1 rounded-full bg-papaya text-ink text-[11px] font-black tracking-[0.2em] uppercase py-2 hover:brightness-110 disabled:opacity-50"
                  data-testid="voice-preview-save"
                >{uploading ? "Saving…" : "Save"}</button>
                <button
                  onClick={discardPreview}
                  className="flex-1 rounded-full bg-white/10 text-cream text-[11px] font-black tracking-[0.2em] uppercase py-2 hover:bg-white/20"
                  data-testid="voice-preview-discard"
                >Discard</button>
              </div>
            </div>
          )}
        </div>

        {/* Uploader + metadata */}
        <div className="rounded-3xl crystal-glass p-5" data-testid="voice-uploader">
          <div className="flex items-center gap-2 mb-3">
            <UploadSimple size={18} className="text-papaya" />
            <p className="text-cream font-black uppercase tracking-[0.2em] text-xs">Upload audio</p>
          </div>

          <label className="block text-cream/70 text-[10px] tracking-[0.3em] uppercase mb-1">Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="My voice memo"
            className="w-full bg-white/5 rounded-2xl px-4 py-2 text-cream text-sm border border-white/10 focus:border-papaya outline-none"
            data-testid="voice-title-input"
          />

          <label className="block text-cream/70 text-[10px] tracking-[0.3em] uppercase mb-1 mt-3">Kind</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="w-full bg-white/5 rounded-2xl px-4 py-2 text-cream text-sm border border-white/10 outline-none"
            data-testid="voice-kind-select"
          >
            {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value} className="bg-ink">{k.label}</option>)}
          </select>

          <label className="flex items-center gap-2 mt-3 cursor-pointer" data-testid="voice-public-toggle">
            <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="accent-papaya" />
            <span className="text-cream/70 text-xs">Share to public gallery</span>
          </label>

          <label className="mt-4 rounded-full bg-white/10 text-cream text-[11px] font-black tracking-[0.2em] uppercase py-3 px-4 hover:bg-white/20 cursor-pointer flex items-center justify-center gap-2" data-testid="voice-file-picker">
            <UploadSimple size={14} />
            {uploading ? "Uploading…" : "Choose MP3 / WAV / M4A / AAC"}
            <input type="file" accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.webm" onChange={onFilePick} className="hidden" />
          </label>
          <p className="text-cream/40 text-[10px] tracking-[0.2em] uppercase mt-3 text-center">
            Max 60 MB · Songs, podcasts, spoken word, business, conference audio.
          </p>
        </div>
      </div>

      {/* Library */}
      <div className="mx-5 mt-8">
        <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">My Library</p>
        <h2 className="text-cream text-xl font-black tracking-tighter mt-1 mb-3">
          {items.length} recording{items.length === 1 ? "" : "s"}
        </h2>
        {loading ? (
          <p className="text-cream/40 text-xs tracking-[0.3em] uppercase animate-pulse">Loading…</p>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/15 p-8 text-center" data-testid="voice-empty">
            <Waveform size={36} className="text-cream/30 mx-auto" weight="duotone" />
            <p className="text-cream font-bold mt-3">No recordings yet.</p>
            <p className="text-cream/50 text-xs mt-1">Record something or upload an MP3 to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {items.map((rec) => (
              <VoiceCard
                key={rec.id} rec={rec}
                renaming={renamingId === rec.id}
                onStartRename={() => { setRenamingId(rec.id); setRenameText(rec.title); }}
                onCancelRename={() => setRenamingId(null)}
                renameText={renameText}
                setRenameText={setRenameText}
                onCommitRename={() => commitRename(rec.id)}
                onTogglePublic={() => togglePublic(rec)}
                onDelete={() => deleteRec(rec.id)}
                onTranscribe={() => transcribe(rec)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="mx-5 mt-8 text-center">
        <Link to="/studio" className="text-cream/40 text-xs tracking-[0.3em] uppercase hover:text-papaya">
          Also try Creator Studio →
        </Link>
      </div>

      <VoiceEffectsPanel open={showFx} onClose={() => setShowFx(false)} />
    </div>
  );
}

function VoiceCard({
  rec, renaming, renameText, setRenameText,
  onStartRename, onCancelRename, onCommitRename, onTogglePublic,
  onDelete, onTranscribe,
}) {
  const url = rec.storage_path?.startsWith("http")
    ? rec.storage_path
    : `${process.env.REACT_APP_BACKEND_URL}/api/files/${rec.storage_path}`;

  const enh = rec.enhancement || {};

  return (
    <div className="rounded-3xl crystal-glass p-4" data-testid={`voice-card-${rec.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {renaming ? (
            <div className="flex gap-2">
              <input
                value={renameText}
                onChange={(e) => setRenameText(e.target.value)}
                autoFocus
                className="flex-1 bg-white/10 rounded-full px-3 py-1.5 text-cream text-sm border border-papaya outline-none"
                data-testid={`voice-rename-input-${rec.id}`}
              />
              <button
                onClick={onCommitRename}
                className="rounded-full bg-papaya text-ink px-3 py-1.5 text-[10px] font-black tracking-[0.2em] uppercase"
                data-testid={`voice-rename-save-${rec.id}`}
              >Save</button>
              <button onClick={onCancelRename} className="rounded-full bg-white/10 text-cream px-3 py-1.5 text-[10px] font-black tracking-[0.2em] uppercase">×</button>
            </div>
          ) : (
            <h3 className="text-cream font-bold truncate" data-testid={`voice-title-${rec.id}`}>{rec.title}</h3>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[9px] tracking-[0.25em] uppercase font-bold text-papaya bg-papaya/15 rounded-full px-2 py-0.5">
              {rec.kind}
            </span>
            <span className="text-[9px] tracking-[0.2em] uppercase text-cream/40">
              {rec.format} · {fmtSize(rec.size_bytes)} · {fmtDuration(rec.duration_sec)}
            </span>
            {rec.is_public ? (
              <span className="text-[9px] uppercase text-teal-300 flex items-center gap-1"><GlobeHemisphereWest size={10} /> Public</span>
            ) : (
              <span className="text-[9px] uppercase text-cream/40 flex items-center gap-1"><Lock size={10} /> Private</span>
            )}
            {enh.status && (
              <span className="text-[9px] tracking-[0.25em] uppercase bg-white/10 text-cream/70 rounded-full px-2 py-0.5">
                enhance · {enh.status}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onStartRename} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-cream/70" title="Rename" data-testid={`voice-rename-btn-${rec.id}`}>
            <PencilSimple size={14} />
          </button>
          <button onClick={onTogglePublic} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-cream/70" title="Toggle public" data-testid={`voice-public-btn-${rec.id}`}>
            {rec.is_public ? <Lock size={14} /> : <GlobeHemisphereWest size={14} />}
          </button>
          <button onClick={onDelete} className="w-8 h-8 rounded-full hover:bg-red-500/20 flex items-center justify-center text-red-400" title="Delete" data-testid={`voice-delete-btn-${rec.id}`}>
            <Trash size={14} />
          </button>
        </div>
      </div>

      <audio controls src={url} className="w-full mt-3" data-testid={`voice-player-${rec.id}`} />

      <div className="flex flex-wrap gap-2 mt-3">
        <button
          onClick={onTranscribe}
          className="rounded-full bg-white/10 text-cream text-[10px] px-3 py-1.5 font-black tracking-[0.2em] uppercase hover:bg-white/20 flex items-center gap-1"
          title="Transcribe with Whisper"
          data-testid={`voice-transcribe-btn-${rec.id}`}
        >
          <TextT size={12} weight="fill" /> Transcribe
        </button>
        <a
          href={url} target="_blank" rel="noopener noreferrer"
          className="rounded-full bg-white/10 text-cream text-[10px] px-3 py-1.5 font-black tracking-[0.2em] uppercase hover:bg-white/20 flex items-center gap-1"
          data-testid={`voice-download-btn-${rec.id}`}
        >
          <PlayCircle size={12} weight="fill" /> Open
        </a>
      </div>

      {rec.transcript && (
        <div className="mt-3 rounded-2xl bg-white/5 border border-white/10 p-3">
          <p className="text-papaya text-[9px] tracking-[0.3em] uppercase font-bold mb-1">Transcript</p>
          <p className="text-cream/80 text-xs whitespace-pre-wrap line-clamp-4">{rec.transcript}</p>
        </div>
      )}
    </div>
  );
}
